<script setup>
import { ref, computed } from 'vue'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { addComplimentaryOfferItemFn } from '@/services/api/restApiCalls'
import { notify } from 'notiwind'

const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()

// Get free dish options from cartStore
const freeDishOptions = computed(() => {
  return cartStore?.CART_DATA?.complimentary_offer?.complimentary_list?.items || []
})

// Get complimentary offer ID from cartStore
const complimentaryOfferId = computed(() => {
  return cartStore?.CART_DATA?.complimentary_offer?.complimentary_offer_id
})

const selectedDish = ref('')
const isDropdownOpen = ref(false)

const handleDishSelection = async (dish) => {
  selectedDish.value = dish.item_name_full || dish.item_name
  
  const complimentaryItemObj = {
    action: 'add_complimentary_offer_item',
    resto_id: restoStore?.RESTO_ID,
    item_id: dish?.item_id,
    complimentary_offer_id: complimentaryOfferId.value || cartStore?.CART_DATA?.complimentary_offer?.complimentary_offer_id
  }
  
  var complimentaryItemFormData = new FormData()
  complimentaryItemFormData.append('cart_data', JSON.stringify(complimentaryItemObj))
  
  try {
    const cart_info = await addComplimentaryOfferItemFn(complimentaryItemFormData)
    cartStore.CART_DATA = cart_info[restoStore?.RESTO_ID]
    cartStore.CAN_DO_CHECKOUT = cart_info?.can_do_checkout
    
    notify(
      {
        group: 'success',
        title: 'Free Gift Added!',
        text: `${dish?.item_name_full || dish?.item_name} added to cart as free gift`
      },
      2000
    )
    selectedDish.value = ''
  } catch (error) {
    notify(
      {
        group: 'error',
        title: 'Error',
        text: 'Failed to add free gift to cart'
      },
      2000
    )
  }
  
  isDropdownOpen.value = false
}

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

// Show free dish component if complimentary_list exists and has items
const showFreeDish = computed(() => {
  return cartStore?.CART_DATA?.complimentary_offer?.complimentary_list?.items?.length > 0
})
</script>

<template>
  <div v-if="showFreeDish" class="mb-4">
    <!-- Title -->
    <h3 class="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
      You got a gift! 
      <span class="text-sm text-gray-500 dark:text-gray-400 font-normal">
        (Select any {{ cartStore?.CART_DATA?.complimentary_offer?.item_count }} free dish from the list below)
      </span>
    </h3>
    <!-- Dropdown -->
    <div class="relative">
      <div class="relative">
        <input
          data-test="free-dish-input"
          v-model="selectedDish"
          type="text"
          placeholder="Select a free dish"
          readonly
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          @click="toggleDropdown"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg 
            class="w-4 h-4 text-gray-400 transform transition-transform duration-200"
            :class="{ 'rotate-180': isDropdownOpen }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      
      <!-- Dropdown menu -->
      <div
        v-if="isDropdownOpen"
        data-test="free-dish-dropdown"
        class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm shadow-lg max-h-60 overflow-auto"
      >
        <div
          v-for="dish in freeDishOptions"
          :key="dish.item_id"
          data-test="free-dish-option"
          class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors duration-150"
          @click="handleDishSelection(dish)"
        >
          <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ dish.item_name_full || dish.item_name }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Click outside to close dropdown -->
    <div
      v-if="isDropdownOpen"
      data-test="free-dish-overlay"
      class="fixed inset-0 z-40"
      @click="closeDropdown"
    ></div>
  </div>
</template>
